import { useState } from "react";
import { Link } from "react-router-dom";
import { Euro, CalendarDays, Droplet, Fuel, Gauge, Route, UserCheck, Activity, CreditCard, Truck } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, Chip, ExportBtn, FilterBar, Field, NSelect, useFetch } from "@/components/common";
import { Input } from "@/components/ui/input";
import { fEur, fKm, fNum, fDate, fMonth } from "@/lib/api";
import { AccOverview } from "@/pages/livraison/Accidents";
import { PersoTypesSub } from "@/components/Conges";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export function ComFilters({ f, setF, full = true, provider = false, testid = "com-filters" }) {
  const u = useFetch("/users", { role: "commercial", with_m2: true });
  const v = useFetch(full ? "/vehicles" : null, { type: "commercial" });
  const set = (k) => (x) => setF({ ...f, [k]: x });
  const y = new Date().getFullYear();
  return (
    <FilterBar testid={testid}>
      <Field label="Commercial"><NSelect value={f.user_id} onChange={set("user_id")} placeholder="Tous" testid="filter-commercial"
        options={(u.data || []).map((x) => ({ value: x.id, label: x.name }))} /></Field>
      {full && <Field label="Véhicule"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Tous" testid="filter-com-vehicle"
        options={(v.data || []).map((x) => ({ value: x.id, label: x.plate }))} /></Field>}
      {full && <Field label="Année"><NSelect value={f.year} onChange={set("year")} placeholder="Toutes" testid="filter-year"
        options={[y, y - 1, y - 2].map((x) => ({ value: String(x), label: String(x) }))} /></Field>}
      {full && <Field label="Mois"><NSelect value={f.month} onChange={(m) => setF({ ...f, month: m, year: f.year || (m ? String(y) : "") })} placeholder="Tous" testid="filter-month"
        options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} /></Field>}
      {provider && <Field label="Fournisseur de carte"><NSelect value={f.provider} onChange={set("provider")} placeholder="Tous" testid="filter-provider"
        options={[{ value: "mooncard", label: "Mooncard" }, { value: "fleetpro", label: "Fleet Pro" }]} /></Field>}
      <Field label="Du"><Input type="date" value={f.date_from || ""} onChange={(e) => set("date_from")(e.target.value)} className="h-10 bg-white" data-testid="filter-com-from" /></Field>
      <Field label="Au"><Input type="date" value={f.date_to || ""} onChange={(e) => set("date_to")(e.target.value)} className="h-10 bg-white" data-testid="filter-com-to" /></Field>
      <div className="flex items-end">
        <button onClick={() => setF({})} className="h-10 px-3 text-sm font-semibold text-slate-500 hover:text-[#0E2D43]" data-testid="filter-com-reset">Réinitialiser</button>
      </div>
    </FilterBar>
  );
}

function Ranking({ rows, field, fmt, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[field] || 0));
  if (!rows.length) return <Empty />;
  return (
    <ol className="space-y-3" data-testid={testid}>
      {rows.map((r, i) => (
        <li key={r.user_id}>
          <Link to={`/commerciaux/equipe/${r.user_id}`} className="block group" data-testid={`${testid}-${r.user_id}`}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold text-[#0E2D43] group-hover:text-[#E0830B] transition-colors"><span className="text-slate-400 mr-2 font-mono">{i + 1}</span>{r.name}</span>
              <span className="tabular-nums font-semibold">{fmt(r[field])}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-[#0E2D43] group-hover:bg-[#F49619] transition-colors" style={{ width: `${((r[field] || 0) / max) * 100}%` }} />
            </div>
          </Link>
        </li>
      ))}
    </ol>
  );
}

export default function ComDashboard() {
  const [f, setF] = useState({});
  const { data, loading } = useFetch("/commerciaux-dashboard", f);
  return (
    <div data-testid="commerciaux-dashboard">
      <PageHeader overline="Véhicules commerciaux" title="Tableau de bord">
        <ExportBtn path="/export/commerciaux" params={f} filename="synthese-commerciaux.xlsx" label="Excel" testid="export-com-xlsx" />
        <ExportBtn path="/export/pdf/commerciaux" params={f} filename="synthese-commerciaux.pdf" label="Synthèse PDF" pdf testid="export-com-pdf" />
      </PageHeader>
      <AccOverview module="commercial" />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="dash-category">
        {[["", "Commerciaux"], ["technicien", "Technicien / Chef de dépôt"]].map(([v, l]) => (
          <button key={v || "com"} onClick={() => setF({ ...f, category: v, user_id: "" })} data-testid={`dash-category-${v || "commercial"}`}
            className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${(f.category || "") === v ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0E2D43]"}`}>{l}</button>
        ))}
      </div>
      <ComFilters f={f} setF={setF} provider />
      {loading || !data ? <Loading /> : <Body d={data} cat={f.category} />}
    </div>
  );
}

function Body({ d, cat }) {
  const f = { category: cat };
  const k = d.kpis;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="kpi-com-spend" label={f.category === "technicien" ? "Dépenses carburant (technicien / responsable)" : "Dépenses carburant (commerciaux)"} value={fEur(k.spend, 0)} sub={`${k.commercials} ${f.category === "technicien" ? "personne(s)" : "commerciaux"}`} icon={Euro} tone="navy" />
        <Kpi testid="kpi-com-mooncard" label="dont Mooncard" value={fEur(k.spend_mooncard, 0)} icon={CreditCard} />
        <Kpi testid="kpi-com-fleetpro" label="dont Fleet Pro" value={fEur(k.spend_fleetpro, 0)} icon={CreditCard} />
        <Kpi testid="kpi-com-logistique" label="Dépenses logistiques (Fleet Pro)" value={k.spend_logistique === null ? "—" : fEur(k.spend_logistique, 0)} sub={k.spend_logistique === null ? "Non concerné par le filtre" : `Total global : ${fEur(k.spend + k.spend_logistique, 0)}`} icon={Truck} />
        <Kpi testid="kpi-com-spend-month" label="Dépenses du mois" value={fEur(k.spend_month, 0)} sub={fMonth(k.month_key)} icon={CalendarDays} delay={1} />
        <Kpi testid="kpi-com-liters" label="Litres consommés" value={`${fNum(k.liters, 0)} L`} icon={Droplet} delay={2} />
        <Kpi testid="kpi-com-ppl" label="Prix moyen pondéré" value={k.ppl ? `${fNum(k.ppl, 3)} €/L` : "—"} icon={Fuel} delay={3} />
        <Kpi testid="kpi-com-km" label="Km parcourus" value={fKm(k.km)} icon={Route} tone="navy" delay={4} />
        <Kpi testid="kpi-com-cost100" label="Coût carburant / 100 km" value={fEur(k.cost100)} sub={k.l100 ? `${fNum(k.l100, 2)} L/100 km` : ""} icon={Gauge} delay={5} />
        <Kpi testid="kpi-com-perso" label="Kilomètres personnels" value={fKm(k.perso_km)} sub={`${k.km ? `${fNum((k.perso_km / k.km) * 100, 1)} % du total · ` : ""}${PersoTypesSub({ t: k.perso_types })}`} icon={UserCheck} delay={6} />
        <Kpi testid="kpi-com-alerts" label="Alertes relevés" value={k.alerts} icon={Activity} tone="danger" delay={7} />
      </div>
      <Panel title="Dépenses mensuelles par fournisseur & prix au litre" className="mb-6" testid="chart-com-monthly">
        {!d.monthly.length ? <Empty /> : (
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={d.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tickFormatter={fMonth} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} €`} width={70} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} domain={["auto", "auto"]} tickFormatter={(v) => v.toFixed(2)} />
                <Tooltip labelFormatter={fMonth} formatter={(v, n) => (n === "Prix/L" ? `${fNum(v, 3)} €` : fEur(v))} />
                <Legend />
                <Bar yAxisId="l" dataKey="mooncard" name="Mooncard" stackId="s" fill="#F49619" />
                <Bar yAxisId="l" dataKey="fleetpro" name="Fleet Pro" stackId="s" fill="#2F80ED" radius={[4, 4, 0, 0]} />
                <Line yAxisId="r" dataKey="ppl" name="Prix/L" stroke="#0E2D43" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Dépenses par commercial" testid="ranking-spend-panel"><Ranking rows={d.ranking_spend} field="spend" fmt={(v) => fEur(v, 0)} testid="ranking-spend" />
          <table className="tbl w-full mt-4 text-xs" data-testid="spend-by-provider"><thead><tr><th>Commercial</th><th className="text-right">Mooncard</th><th className="text-right">Fleet Pro</th></tr></thead>
            <tbody>{d.ranking_spend.map((r) => <tr key={r.user_id}><td>{r.name}</td><td className="text-right tabular-nums">{fEur(r.spend_mooncard, 0)}</td><td className="text-right tabular-nums">{fEur(r.spend_fleetpro, 0)}</td></tr>)}</tbody></table>
        </Panel>
        <Panel title="Classement des kilométrages" testid="ranking-km-panel"><Ranking rows={d.ranking_km} field="km" fmt={fKm} testid="ranking-km" /></Panel>
        <Panel title="Alertes relevés" flush testid="com-alerts">
          {!d.alerts.length ? <Empty text="Aucune alerte" /> : (
            <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {d.alerts.map((a) => (
                <li key={a.id} className="px-4 py-2.5 text-sm" data-testid={`com-alert-${a.type}`}>
                  <div className="flex justify-between gap-2"><span className="font-semibold text-[#0E2D43]">{a.user_name}</span><span className="text-xs text-slate-400">{fDate(a.date)}</span></div>
                  <div className="flex items-center gap-2 mt-1"><Chip cls={a.severity === "danger" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{a.label}</Chip><span className="text-xs text-slate-500 truncate">{a.detail}</span></div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
